import { StoreId } from "../../decorators/StoreIdDecorator";
import { getObjectStoreId } from "../../decorators/StoreIdHelper";
import AcceleratorDoc, { AcceleratorDocStoreKey } from "../AcceleratorDoc";
import Model, { ModelStoreKey } from "../Model";
import { Hydratable, HydrateFunction } from "./Hydratable";
import { HydratableProxy } from "./HydratableProxy";

export { HydratableFactory };

// TODO: use `new` prefix for factory methods.
class HydratableFactory {
  static getAcceleratorDoc(
    hydrate: HydrateFunction<AcceleratorDoc>,
    storeId: AcceleratorDocStoreKey,
  ): Hydratable<AcceleratorDoc, AcceleratorDocStoreKey> {
    return new GenericHydratableProxy<AcceleratorDoc, AcceleratorDocStoreKey>(
      hydrate,
      storeId,
    );
  }
  static getModel(
    hydrate: HydrateFunction<Model>,
    storeId: ModelStoreKey,
  ): Hydratable<Model, ModelStoreKey> {
    return new GenericHydratableProxy<Model, ModelStoreKey>(hydrate, storeId);
  }
}

class GenericHydratable<T, K> implements Hydratable<T, K> {
  @StoreId
  _storeId: K;
  _hydrated: T | undefined;
  _hydrate: HydrateFunction<T>;
  _hydratePromise?: Promise<T | undefined>;

  constructor(hydrate: HydrateFunction<T>, storeId: K) {
    this._hydrate = hydrate;
    this._storeId = storeId;
  }

  isHydrated(autoHydrate: boolean | undefined = true): boolean {
    // console.trace(this._hydrated);
    if (this._hydrated == undefined && autoHydrate) {
      this.hydrate();
    }
    return this._hydrated != undefined;
  }

  getHydrated(): T {
    if (!this.isHydrated(false)) {
      throw new Error("Object not hydrated.");
    }
    return this._hydrated!;
  }

  async hydrate(): Promise<T | undefined> {
    if (this._hydratePromise == undefined) {
      this._hydratePromise = this._hydrate();
      this._hydrated = await this._hydratePromise;
    }

    return this._hydrated;
  }

  manuallyHydrate(object: T | undefined): Hydratable<T, K> {
    this._hydrated = object;
    if (object != undefined) {
      this._hydrate = () => new Promise((res) => res(this._hydrated));
      this._hydratePromise = this._hydrate();
    }
    this._storeId = getObjectStoreId(object);
    return this;
  }
}

class GenericHydratableProxy<T, K>
  extends GenericHydratable<T, K>
  implements HydratableProxy<T, K>
{
  get(target: any, prop: any): any {
    if (!this.isHydrated(false)) {
      throw new Error("Object not hydrated.");
    }
    return target[prop];
  }

  set(target: any, prop: any, value: any): any {
    if (!this.isHydrated(false)) {
      throw new Error("Object not hydrated.");
    }
    target[prop] = value;
    // Indicate success
    return true;
  }

  has(target: any, prop: any): boolean {
    if (!this.isHydrated(false)) {
      throw new Error("Object not hydrated.");
    }
    return prop in target || target.hasItem(prop);
  }
}
